import React, { useState } from 'react';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { getLocalizedParameterCategory } from '../common/localization';
import NativeSelect from '@mui/material/NativeSelect';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';

// Native <option> elements only reliably support a subset of CSS across browsers
// (e.g. Chrome ignores text-decoration on options), so blocked options are grayed out via
// color + background-color (both well supported) and get a text suffix so the "unavailable"
// state is unambiguous everywhere, not just relying on styling.
const blockedOptionStyle = { color: '#9e9e9e', backgroundColor: '#f0f0f0', fontStyle: 'italic' };

export default function ParameterSelector(props) {
    const category = props.category;
    const values = props.values[props.category];
    const handleChange = props.handleChange;
    const selected = props.selected ? props.selected.id : 0;
    const showNoSelectionOption = !props.requireValue;
    const { t } = useTranslation();
    // Ids in this list are still shown (strikethrough) but cannot be selected; selecting one
    // opens an explanatory dialog instead of propagating the change. Used e.g. to keep an order's
    // delivery method visible but blocked when cargo pricing is not configured for the delivery
    // address, while leaving it fully selectable elsewhere (location/customer default settings).
    const blockedIds = props.blockedIds || [];
    const [blockedDialogOpen, setBlockedDialogOpen] = useState(false);

    if (!props.values || props.values.length === 0) return null;

    //custom option erotus listan kärkeen
    const topOption = values.find((v) => v.id === 1);
    const optionValues = values.filter((v) => v.id !== 1);

    const isBlocked = (id) => blockedIds.includes(id);

    const onSelectChange = (evt) => {
        const value = parseInt(evt.target.value);
        if (isBlocked(value)) {
            setBlockedDialogOpen(true);
            return;
        }
        handleChange(category, value);
    };

    return (
        <>
            <FormControl sx={{ mt: 2, mx: 1, width: '95%' }}>
                <InputLabel id={category + '-label'}>{getLocalizedParameterCategory(category)}</InputLabel>
                <NativeSelect
                    disabled={props.disabled}
                    id={category}
                    name={category}
                    required={!!props.required}
                    error={!!props.required && selected === 0}
                    value={selected}
                    onChange={onSelectChange}>
                    {showNoSelectionOption && (
                        <option key={0} value={0}>
                            {t('general.optionNotSelected')}
                        </option>
                    )}
                    {topOption && (
                        <option key={topOption.id} value={topOption.id} style={isBlocked(topOption.id) ? blockedOptionStyle : undefined}>
                            {topOption.value}
                            {isBlocked(topOption.id) ? t('general.optionNotAvailableSuffix') : ''}
                        </option>
                    )}
                    {optionValues.map((v) => (
                        <option key={v.id} value={v.id} style={isBlocked(v.id) ? blockedOptionStyle : undefined}>
                            {v.value}
                            {isBlocked(v.id) ? t('general.optionNotAvailableSuffix') : ''}
                        </option>
                    ))}
                </NativeSelect>
            </FormControl>
            {blockedDialogOpen && (
                <Dialog open={blockedDialogOpen} onClose={() => setBlockedDialogOpen(false)}>
                    <DialogTitle>{t('general.optionNotAvailableTitle')}</DialogTitle>
                    <DialogContent>{props.blockedMessage || t('general.optionNotAvailableGenericReason')}</DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBlockedDialogOpen(false)}>{t('buttons.close')}</Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}
